<template>
	<v-form ref="absenceForm" @submit.prevent>
		<!-- SECTION: INFORMATION -->
		<p class="section-label">{{ $t('notification.category_info') }}</p>
		<v-divider class="mb-5"></v-divider>

		<v-row dense align="center" class="mb-5">
			<v-col cols="6">
				<v-autocomplete
					dense
					hide-details="auto"
					:label="$t('driver')"
					item-title="name"
					item-value="_id"
					v-model="localAbsence.driverName"
					variant="underlined"
					:disabled="true"
				/>
			</v-col>

			<v-col cols="6">
				<v-autocomplete
					dense
					hide-details="auto"
					:label="$t('planning_type')"
					:items="reasons"
					item-title="text"
					item-value="value"
					v-model="localAbsence.type"
					:rules="reasonRule"
					variant="underlined"
				/>
			</v-col>

			<v-col cols="6">
				<v-autocomplete
					dense
					hide-details="auto"
					:label="$t('user_edit_status')"
					:items="getStatusOptions"
					v-model="localAbsence.status"
					item-title="name"
					item-value="id"
					variant="underlined"
				/>
			</v-col>

			<v-col cols="6" v-if="localAbsence.type === 'offtime' || localAbsence.type === 'reduce'">
				<v-select
					dense
					hide-details="auto"
					:label="$t('absence.priority')"
					:items="priorities"
					item-title="text"
					item-value="value"
					v-model="localAbsence.priority"
					variant="underlined"
				/>
			</v-col>
		</v-row>

		<v-row dense class="mb-2">
			<v-col cols="6" class="d-flex align-center">
				<v-switch
					v-model="localAbsence.entireDay"
					:label="$t('absence.entireday')"
					color="#115151"
					hide-details
					density="compact"
					@update:model-value="onEntireDayToggle"
				/>
			</v-col>
		</v-row>

		<v-row dense align="center">
			<v-col cols="6">
				<date-entry-form
					ref="startDate"
					:model-value="localAbsence.startDate"
					:label="'absence.startDate'"
					:min="getMinStartDate()"
					:rules="startDateRule(localAbsence)"
					@update:model-value="(v) => updateAbsenceDate('startDate', v)"
					@blur="triggerCrossValidation"
					@update:modelValue="onDateChange"
				/>
			</v-col>
			<v-col cols="6" v-show="!localAbsence.entireDay">
				<TimeEntryForm
					ref="startTime"
					v-model="localAbsence.startTime"
					:label="'absence.startTime'"
					:rules="ruleStartTime()"
					class="time-entry-p"
					@blur="onTimeBlur"
				/>
			</v-col>
		</v-row>

		<v-row dense align="center">
			<v-col cols="6">
				<date-entry-form
					ref="endDate"
					:model-value="localAbsence.endDate"
					:label="'absence.endDate'"
					:min="getMinEndDate(localAbsence.startDate)"
					:rules="endDateRule(localAbsence)"
					@update:model-value="(v) => updateAbsenceDate('endDate', v)"
					@blur="triggerCrossValidation"
					@update:modelValue="onDateChange"
				/>
			</v-col>
			<v-col cols="6" v-show="!localAbsence.entireDay">
				<TimeEntryForm
					ref="endTime"
					v-model="localAbsence.endTime"
					:label="'absence.endTime'"
					:rules="ruleEndTime()"
					class="time-entry-p"
					@blur="onTimeBlur"
				/>
			</v-col>
		</v-row>

		<!-- DRIVER COMMENT (READ-ONLY) -->
		<v-row dense v-if="localAbsence.description" class="mt-5">
			<v-col cols="12">
				<v-textarea
					v-model="localAbsence.description"
					dense
					auto-grow
					rows="4"
					hide-details
					readonly
					:disabled="true"
					:label="$t('absence.description')"
					variant="underlined"
				/>
			</v-col>
		</v-row>

		<!-- OVERLAP -->
		<v-row dense v-if="localAbsence.isOverlappingAbsence" class="mt-5">
			<v-col cols="12">
				<div class="overlap-warning">
					<v-icon small class="mr-1">mdi-information-outline</v-icon>
					{{ localAbsence.overlapText }}
				</div>
			</v-col>
		</v-row>

		<!-- COMMENT -->
		<p class="section-label mt-5">
			{{ $t('comments') }}
		</p>
		<v-textarea v-model="localAbsence.changeComment" dense auto-grow rows="2" hide-details :placeholder="$t('planning_type_here')" variant="underlined" />
	</v-form>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { DateTime } from 'luxon';
import i18n from '@/plugins/i18n';
import DateEntryForm from '../../../../../../web/components/DateAndTime/DateEntryForm/DateEntryForm.vue';
import TimeEntryForm from '../../../../../../web/components/DateAndTime/TimeEntryForm/TimeEntryForm.vue';
import { ABSENCE_TYPES } from '../../../absenceStatistics/constants/absenceTypes.js';
import { createTimeRule } from '../../../../common/validations/timeValidations.js';
import useDateTime from '../../../../../../web/components/composables/useDateTime';
import { parseTimeToMinutes } from '@/rennovation/shared/utils/time-utils';

const ABSENCE_TYPES_WITH_PRIORITY = [ABSENCE_TYPES.OFFTIME, ABSENCE_TYPES.REDUCE];
const DEFAULT_ABSENCE_PRIORITY = 1;

export default {
	name: 'UserAbsenceDialog',
	components: { DateEntryForm, TimeEntryForm },

	props: {
		modelValue: {
			type: Object,
			default: () => ({
				type: null,
				status: 'pending',
				startDate: new Date().toISOString().split('T')[0],
				startTime: null,
				endDate: new Date().toISOString().split('T')[0],
				endTime: null,
				priority: DEFAULT_ABSENCE_PRIORITY,
				files: [],
				isOverlappingAbsence: false,
				overlapText: '',
				entireDay: true,
			}),
		},
		disabled: {
			type: Boolean,
			default: false,
		},
	},

	emits: ['update:modelValue'],
	setup() {
		const { formatDate, formatSecondsDuration } = useDateTime();

		return {
			formatDate,
			formatSecondsDuration,
		};
	},

	data() {
		return {
			localAbsence: { ...this.modelValue },
			reasons: [
				{ value: ABSENCE_TYPES.ILLNESS, text: this.$t('absence.type.illness') },
				{ value: ABSENCE_TYPES.ILLNESS_RELATIVES, text: this.$t('absence.type.illnessRelatives') },
				{ value: ABSENCE_TYPES.JUSTIFIED_ABSENCE, text: this.$t('absence.type.justifiedAbsence') },
				{ value: ABSENCE_TYPES.WORK_ACCIDENT, text: this.$t('absence.type.workAccident') },
				{ value: ABSENCE_TYPES.NONWORK_ACCIDENT, text: this.$t('absence.type.nonworkAccident') },
				{ value: ABSENCE_TYPES.VACATION, text: this.$t('absence.type.vacation') },
				{ value: ABSENCE_TYPES.UNPAID_VACATION, text: this.$t('absence.type.unpaidVacation') },
				{ value: ABSENCE_TYPES.SERVICE, text: this.$t('absence.type.service') },
				{ value: ABSENCE_TYPES.OFFTIME, text: this.$t('absence.type.offtime') },
				{ value: ABSENCE_TYPES.REDUCE, text: this.$t('absence.type.reduce') },
				{ value: ABSENCE_TYPES.EDUCATION, text: this.$t('absence.type.education') },
				{ value: ABSENCE_TYPES.WORK, text: this.$t('absence.type.work') },
				{ value: ABSENCE_TYPES.MATERNITY, text: this.$t('absence.type.maternity') },
				{ value: ABSENCE_TYPES.PATERNITY, text: this.$t('absence.type.paternity') },
				{ value: ABSENCE_TYPES.PAID_ABSENCE, text: this.$t('absence.type.paidAbsence') },
				{ value: ABSENCE_TYPES.WORK_PREFERENCE, text: this.$t('absence.type.workPreference') },
				{ value: 'seniorityGift', text: this.$t('absence.type.seniorityGift') },
			],
			priorities: [
				{ value: 1, text: this.$t('absence.priority.nice_to_have') },
				{ value: 2, text: this.$t('absence.priority.medium') },
				{ value: 3, text: this.$t('absence.priority.important') },
			],
		};
	},

	computed: {
		...mapState({
			firstPastDay: (state) => state.days.firstPastDay,
			locale: (state) => state.profile.locale,
			setup: (state) => state.setup.setup,
		}),
		...mapGetters({
			getMinStartDate: 'days/getMinStartDate',
			getMinEndDate: 'days/getMinEndDate',
			toUTCFromLocalDateTime: 'days/toUTCFromLocalDateTime',
		}),

		getStatusOptions() {
			return [
				{ id: 'pending', name: this.$t('absence.pending') },
				{ id: 'approved', name: this.$t('absence.approved') },
				{ id: 'rejected', name: this.$t('absence.rejected') },
			];
		},

		reasonRule() {
			return [(v) => !!v || i18n.t('absence.please_choose_reason')];
		},

		startDateRule() {
			return (item) => [
				(v) => !!v || i18n.t('absence.please_choose_date'),
				(v) => {
					if (!v || !item.endDate) {
						return true;
					}
					const start = this.parseDisplayDate(v);
					const end = this.parseDisplayDate(item.endDate);
					if (!start?.isValid || !end?.isValid) {
						return true;
					}
					return start <= end || i18n.t('absence.error_start_after_end');
				},
			];
		},

		endDateRule() {
			return (item) => [
				(v) => !!v || i18n.t('absence.please_choose_date'),
				(v) => {
					if (!v || !item.startDate) {
						return true;
					}
					const end = this.parseDisplayDate(v);
					const start = this.parseDisplayDate(item.startDate);
					if (!end?.isValid || !start?.isValid) {
						return true;
					}
					return end >= start || i18n.t('absence.error_end_before_start');
				},
			];
		},

		invalidOperatingDay() {
			if (this.localAbsence.entireDay || this.localAbsence.type !== 'workPreference') return '';
			if (!this.localAbsence.startDate || !this.localAbsence.endDate || !this.localAbsence.startTime || !this.localAbsence.endTime) return '';

			const dayInSeconds =
				this.toUTCFromLocalDateTime(this.localAbsence.startDate)
					.plus({
						days: 1,
					})
					.toSeconds() - this.toUTCFromLocalDateTime(this.localAbsence.startDate).toSeconds();

			const absence = this.localAbsence;
			const operatingDayStartTime = this.setup?.planning_operatingDayStartDelta
				? this.formatSecondsDuration(this.setup.planning_operatingDayStartDelta)
				: '00:00';

			const validStartTime = this.toUTCFromLocalDateTime(absence.startDate, operatingDayStartTime).toSeconds();

			const dayStartBeforeOperatingDay = this.toUTCFromLocalDateTime(absence.startDate, absence.startTime).toSeconds() < validStartTime;
			const dayEndBeforeOperatingDay = this.toUTCFromLocalDateTime(absence.endDate, absence.endTime).toSeconds() <= validStartTime;

			if (dayStartBeforeOperatingDay && dayEndBeforeOperatingDay) {
				const extraSeconds = absence.endTime === operatingDayStartTime ? 60 : 0;

				if (
					this.toUTCFromLocalDateTime(absence.endDate, absence.endTime).toSeconds() -
						this.toUTCFromLocalDateTime(absence.startDate, absence.startTime).toSeconds() <
					dayInSeconds + extraSeconds
				) {
					return '';
				}
			}

			// check if start time starts before start of operating day
			if (this.toUTCFromLocalDateTime(absence.startDate, absence.startTime).toSeconds() < validStartTime) {
				return 'Invalid';
			}

			let validEndTime = validStartTime + dayInSeconds;
			if (absence.startTime === operatingDayStartTime || absence.endTime === operatingDayStartTime) {
				validEndTime += 60;
			}

			if (absence.endDate && absence.endTime) {
				if (this.toUTCFromLocalDateTime(absence.endDate, absence.endTime).toSeconds() >= validEndTime) {
					return 'Invalid';
				}
			}

			return '';
		},
	},

	watch: {
		localAbsence: {
			deep: true,
			handler(val) {
				this.$emit('update:modelValue', { ...val });
			},
		},
		modelValue: {
			deep: true,
			handler(val) {
				if (val?.id !== this.localAbsence?.id) {
					this.localAbsence = { ...val };
				}
			},
		},
		'localAbsence.startDate': {
			deep: true,
			handler() {
				if (this.localAbsence.startDate > this.localAbsence.endDate) {
					this.localAbsence.endDate = this.localAbsence.startDate;
				}
			},
		},
	},

	methods: {
		...mapActions({
			checkAbsenceOverlap: 'users/checkAbsenceOverlap',
			loadFirstPastDay: 'days/loadFirstPastDay',
			loadSetup: 'setup/loadSetup',
		}),

		validate() {
			return this.$refs.absenceForm.validate();
		},

		reset() {
			this.localAbsence = { ...this.modelValue };
			this.$refs.absenceForm?.resetValidation();
		},

		resetValidation() {
			this.$refs.absenceForm.resetValidation();
		},

		parseDisplayDate(dateStr) {
			if (!dateStr) {
				return null;
			}
			if (typeof dateStr !== 'string') {
				return dateStr.isValid ? dateStr : null;
			}
			if (/^\d{2}\/\d{2}\/\d{2}$/.test(dateStr)) {
				return DateTime.fromFormat(dateStr, 'MM/dd/yy');
			}
			if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
				return DateTime.fromFormat(dateStr, 'MM/dd/yyyy');
			}
			return DateTime.fromISO(dateStr);
		},

		updateAbsenceDate(field, value) {
			this.localAbsence = { ...this.localAbsence, [field]: value };
			this.$nextTick(() => {
				this.triggerCrossValidation();
			});
		},

		triggerCrossValidation() {
			this.$nextTick(() => {
				this.$refs.startDate?.validate?.();
				this.$refs.endDate?.validate?.();
				this.$refs.startTime?.validate?.();
				this.$refs.endTime?.validate?.();
			});
		},

		ruleStartTime() {
			return [
				(v) => {
					if (!this.localAbsence.entireDay && !v) {
						return i18n.t('absence.start_time_required');
					}
					return true;
				},
				(v) => {
					const { endTime, startDate, endDate } = this.localAbsence;
					if (!v || !endTime) return true;

					const sameDay = !startDate || !endDate || startDate === endDate;
					if (!sameDay) return true;

					return parseTimeToMinutes(v) < parseTimeToMinutes(endTime) || i18n.t('planning_start_time_must_be_before_end_time_start');
				},
				(v) =>
					createTimeRule({
						item: this.localAbsence,
						fieldType: 'start',
						firstPastDay: this.firstPastDay,
						toUTCFromLocalDateTime: this.toUTCFromLocalDateTime,
					})(v),
				() => !this.invalidOperatingDay || i18n.t('absence.invalid_operating_date'),
			];
		},

		ruleEndTime() {
			return [
				(v) => {
					if (!this.localAbsence.entireDay && !v) {
						return i18n.t('absence.end_time_required');
					}
					return true;
				},
				(v) => {
					const { startTime, startDate, endDate } = this.localAbsence;
					if (!v || !startTime) return true;

					const sameDay = !startDate || !endDate || startDate === endDate;
					if (!sameDay) return true;

					return parseTimeToMinutes(v) > parseTimeToMinutes(startTime) || i18n.t('planning_start_time_must_be_before_end_time_end');
				},
				(v) =>
					createTimeRule({
						item: this.localAbsence,
						fieldType: 'end',
						firstPastDay: this.firstPastDay,
						toUTCFromLocalDateTime: this.toUTCFromLocalDateTime,
					})(v),
				() => !this.invalidOperatingDay || i18n.t('absence.invalid_operating_date'),
			];
		},

		onDateChange() {
			this.triggerCrossValidation();
			this.checkForAbsenceOverlap(this.localAbsence);
		},

		onTimeBlur() {
			this.$nextTick(() => {
				this.$refs.startTime?.validate?.();
				this.$refs.endTime?.validate?.();
			});
			this.checkForAbsenceOverlap(this.localAbsence);
		},

		async checkForAbsenceOverlap(item) {
			if (!item.startDate || !item.endDate || !item.user || !item.entireDay) {
				this.localAbsence.isOverlappingAbsence = false;
				return;
			}

			const response = await this.checkAbsenceOverlap(item);
			const { isOverlapping, overlaps = [] } = response;

			this.localAbsence.isOverlappingAbsence = isOverlapping;

			if (isOverlapping && overlaps.length) {
				const parts = overlaps.map((o) =>
					this.$t('description_absence_overlap_warning_user', {
						status: this.$t(`absence.${o.status}`),
						type: this.$t(`absence.type.${o.type}`),
						start: o.startDate ? this.formatDate(o.startDate) : null,
						end: o.endDate ? this.formatDate(o.endDate) : null,
					})
				);
				this.localAbsence.overlapText = this.$t('absence_overlap_warning', { overlaps: parts.join(', ') });
			}
		},

		onEntireDayToggle(val) {
			if (val) {
				this.localAbsence.startTime = null;
				this.localAbsence.endTime = null;
				this.checkForAbsenceOverlap(this.localAbsence);
			}
			this.triggerCrossValidation();
		},
	},

	async beforeMount() {
		await this.loadSetup();
		await this.loadFirstPastDay();
	},
};
</script>

<style scoped>
.section-label {
	font-size: 20px;
	font-weight: 400;
	letter-spacing: 0.25px;
	line-height: 32px;
}

.field-group {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.field-group-label {
	font-size: 12px;
	color: rgba(0, 0, 0, 0.6);
}

.overlap-warning {
	display: flex;
	align-items: center;
	padding: 8px 10px;
	background: #e3f2fd;
	border-radius: 4px;
	font-size: 13px;
	color: #1565c0;
}
</style>
